// QuestionView.tsx - Pure component with all state in parent
import React, { useEffect, useRef } from "react";
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  HelpCircle,
  AlertCircle,
  Volume2,
  PauseCircle,
  PlayCircle,
  Pause,
  Play,
} from "lucide-react";
import { detectTextInfo } from "@/helpers/detectTextInfo";
import { useSpeechSynthesis } from "@/helpers/useSpeechSynthesis";
import { playSound } from "./playSound";
import { getWordIcon } from "./quizdata/wordIcons";

// Types
export interface OptionItem {
  label: string;
  value: string;
  icon?: string;
  imageUrl?: string;
  imageAlt?: string;
}

export type QuestionOption = string | OptionItem;

export interface Question {
  id: number;
  question: string;
  questionIcon?: string;
  questionImage?: string;
  options: QuestionOption[];
  answer: string;
  explanation?: string;
}

// Quiz State Interface - to be managed by parent
export interface QuizState {
  currentQuestionIndex: number;
  selected: string | null;
  showAnswer: boolean;
  score: number;
  correctCount: number;
  isCompleted: boolean;
  result: QuizResult | null;
}

export interface QuizResult {
  score: number;
  passed: boolean;
  correctAnswers: number;
  totalQuestions: number;
}

interface QuestionViewProps {
  // Question data
  question: Question;
  currentQuestion: number;
  totalQuestions: number;
  
  // Quiz state from parent
  selected: string | null;
  showAnswer: boolean;
  score: number;
  
  
  // Quiz state from parent
  quizState: QuizState;
  onQuizStateChange: (state: Partial<QuizState>) => void;
  
  // UI props
  primaryColor?: string;
  isInDialog?: boolean;
  autoAdvance?: boolean;
  
  // Callbacks
  onSelect: (value: string) => void;
  onNext: () => void;
  onEmergencyPause?: (isPaused: boolean) => void;
  onComplete?: (result: QuizResult) => void;
}

// Helper functions
const normalizeOption = (option: QuestionOption): OptionItem => {
  if (typeof option === 'string') {
    return {
      label: option,
      value: option,
      icon: getWordIcon(option),
    };
  }
  return option;
};

const getOptionValue = (option: QuestionOption): string => {
  if (typeof option === 'string') return option;
  return option.value || option.label;
};

const getOptionLabel = (option: QuestionOption): string => {
  if (typeof option === 'string') return option;
  return option.label || option.value;
};

const getOptionIcon = (option: QuestionOption): string | undefined => {
  if (typeof option === 'string') {
    return getWordIcon(option);
  }
  return option.icon || getWordIcon(option.label);
};

const getOptionImage = (option: QuestionOption): string | undefined => {
  if (typeof option === 'string') return undefined;
  return option.imageUrl;
};

const getOptionImageAlt = (option: QuestionOption): string | undefined => {
  if (typeof option === 'string') return undefined;
  return option.imageAlt || option.label;
};

// Media Renderer Component
const OptionMediaRenderer = ({ 
  icon, 
  imageUrl, 
  imageAlt,
  size = 'md'
}: { 
  icon?: string; 
  imageUrl?: string; 
  imageAlt?: string;
  size?: 'sm' | 'md' | 'lg';
}) => {
  const [imageError, setImageError] = React.useState(false);
  
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10 md:w-12 md:h-12',
    lg: 'w-14 h-14 md:w-16 md:h-16'
  };

  const textSizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl md:text-3xl',
    lg: 'text-3xl md:text-4xl'
  };

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={imageAlt || 'Option icon'}
        className={`${sizeClasses[size]} object-contain rounded-lg flex-shrink-0 border border-gray-100 p-1 bg-white shadow-sm`}
        loading="lazy"
        onError={() => setImageError(true)}
        crossOrigin="anonymous"
      />
    );
  }

  if (icon) {
    return (
      <span 
        className={`${sizeClasses[size]} flex items-center justify-center flex-shrink-0 ${textSizeClasses[size]}`}
        role="img" 
        aria-label={imageAlt || ''}
      >
        {icon}
      </span>
    );
  }

  return null;
};

export default function QuestionView({
   question,
  currentQuestion,
  totalQuestions,
  selected,
  showAnswer,
  score,
  quizState,
  onQuizStateChange,
  primaryColor = "bg-emerald-600",
  isInDialog = false,
  autoAdvance = true,
  onSelect,
  onNext,
  onEmergencyPause,
  onComplete,
}: QuestionViewProps) {
  const { speak, stop, isSpeaking } = useSpeechSynthesis();
  const [isExplanationExpanded, setIsExplanationExpanded] = React.useState(true);
  const [isEmergencyPaused, setIsEmergencyPaused] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(15);
  const [timerStopped, setTimerStopped] = React.useState(false);
  const [showResult, setShowResult] = React.useState(false);
  const [correctAnswer, setCorrectAnswer] = React.useState(false);
  const [shuffledOptions, setShuffledOptions] = React.useState<QuestionOption[]>([]);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  const isLastQuestion = currentQuestion + 1 === totalQuestions;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  // Reset on question change
  useEffect(() => {
    setTimeLeft(15);
    setTimerStopped(false);
    setIsEmergencyPaused(false);
    setShowResult(false);
    setCorrectAnswer(false);
    stop();
  }, [question.id, stop]);

  // Timer
  useEffect(() => {
    if (timerStopped || showAnswer || isEmergencyPaused || timeLeft <= 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCorrectAnswer(false);
          setShowResult(true);
          playSound(false);
          onSelect("");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerStopped, showAnswer, isEmergencyPaused, onSelect]);

useEffect(() => {
  const shuffleArray = (array: QuestionOption[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  setShuffledOptions(shuffleArray(question.options));
}, [question.id, question.options]);


  // Auto advance
  useEffect(() => {
    if (!showResult || !autoAdvance) return;
    const t = setTimeout(() => {
      setShowResult(false);
      onNext();
    }, 1400);
    return () => clearTimeout(t);
  }, [showResult, onNext, autoAdvance]);

  // Speak question
  useEffect(() => {
    if (!question?.question) return;
    const timeout = setTimeout(() => speak(question.question), 250);
    return () => {
      clearTimeout(timeout);
      stop();
    };
  }, [question.id, speak, stop]);

  const toggleEmergencyPause = () => {
    const newState = !isEmergencyPaused;
    setIsEmergencyPaused(newState);
    if (newState) stop();
    onEmergencyPause?.(newState);
  };

  const handleOptionSelect = (option: QuestionOption) => {
    if (showAnswer || isEmergencyPaused) return;
    const value = getOptionValue(option);
    const isCorrect = value === question.answer;
    setCorrectAnswer(isCorrect);
    setShowResult(true);
    playSound(isCorrect);
    onSelect(value);
  };

  const toggleSpeech = () => {
    if (isSpeaking) stop();
    else speak(question.question);
  };

  const toggleTimer = () => setTimerStopped((v) => !v);

  // Render options
  const renderOptions = () => {
    return shuffledOptions.map((option, index) => {
      const normalized = normalizeOption(option);
      const value = getOptionValue(option);
      const label = getOptionLabel(option);
      const icon = getOptionIcon(option);
      const imageUrl = getOptionImage(option);
      const imageAlt = getOptionImageAlt(option);
      const isCorrect = value === question.answer;
      const isSelected = value === selected;

      let optionClasses = "w-full rounded-xl border p-3 text-left transition-all duration-200 flex items-center justify-between gap-3";

      if (showAnswer) {
        if (isCorrect) optionClasses += " bg-green-50 border-green-400 ring-1 ring-green-400";
        else if (isSelected) optionClasses += " bg-red-50 border-red-400 ring-1 ring-red-400";
        else optionClasses += " bg-gray-50 border-gray-200";
      } else {
        optionClasses += " hover:border-gray-400 hover:shadow-sm bg-white cursor-pointer";
      }

      return (
        <button
          key={value || index}
          onClick={() => handleOptionSelect(option)}
          disabled={showAnswer || isEmergencyPaused}
          className={optionClasses}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${!showAnswer ? 'bg-gray-100 text-gray-600' : isCorrect ? 'bg-green-500 text-white' : isSelected ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
              {String.fromCharCode(65 + index)}
            </span>
            
            <OptionMediaRenderer 
              icon={icon} 
              imageUrl={imageUrl} 
              imageAlt={imageAlt}
              size="sm"
            />
            
            <span className="text-sm md:text-base font-medium break-words">{label}</span>
          </div>

          {showAnswer && isCorrect && <CheckCircle2 size={18} className="text-green-600 shrink-0" />}
          {showAnswer && isSelected && !isCorrect && <XCircle size={18} className="text-red-600 shrink-0" />}
        </button>
      );
    });
  };

  // ====================== DIALOG MODE ======================
  if (isInDialog) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${primaryColor} text-white flex items-center justify-center font-bold text-sm`}>
                Q{currentQuestion + 1}
              </div>
              <div>
                <p className="text-xs text-gray-500">Question</p>
                <p className="font-semibold text-sm">{currentQuestion + 1}/{totalQuestions}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-gray-500">Score</p>
                <p className="font-bold text-xl">{score}</p>
              </div>
              <button
                onClick={toggleEmergencyPause}
                className={`p-3 rounded-xl transition-all flex items-center justify-center ${
                  isEmergencyPaused ? "bg-amber-500 text-white" : "bg-red-100 hover:bg-red-200 text-red-600"
                }`}
                title={isEmergencyPaused ? "Resume" : "Emergency Pause"}
              >
                {isEmergencyPaused ? <Play size={20} /> : <Pause size={20} />}
              </button>
            </div>
          </div>

          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full transition-all duration-500 ${primaryColor}`} style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Question */}
        <div className="mb-6 w-full rounded-xl border p-5 bg-yellow-100 shadow-sm relative">
          {isEmergencyPaused && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl z-10">
              <div className="text-white text-center">
                <Pause size={48} className="mx-auto mb-3" />
                <p className="font-bold text-xl">Quiz Paused</p>
              </div>
            </div>
          )}

          <div className="flex justify-end mb-3">
            <button onClick={toggleSpeech} className="px-4 py-2 text-gray-500 hover:text-gray-700 transition">
              {isSpeaking ? <Volume2 className="w-5 h-5 text-green-600 animate-pulse" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>
          
          <h3 className={`${detectTextInfo(question.question).direction === "rtl" ? "text-right text-4xl font-arabic font-extrabold" : "text-left text-lg font-semibold"} leading-relaxed break-words`}>
            {question.question}
          </h3>
        </div>

        {/* Options */}
        <div className="space-y-2.5 mb-6">
          {renderOptions()}
        </div>

        {/* Timer */}
        {!showAnswer && (
          <div className="flex items-center gap-3 mb-6 px-1">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 transition-all duration-1000" style={{ width: `${(timeLeft / 15) * 100}%` }} />
            </div>
            <span className="font-bold text-sm tabular-nums">⏱ {timeLeft}s</span>
            <button onClick={toggleTimer} className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition" disabled={isEmergencyPaused}>
              {timerStopped ? <PlayCircle size={20} /> : <PauseCircle size={20} />}
            </button>
          </div>
        )}

        {/* Explanation */}
        {showAnswer && question.explanation && (
          <div id="explanation-section" className="mb-6 rounded-xl bg-blue-50 border border-blue-200 overflow-hidden">
            <button
              onClick={() => setIsExplanationExpanded(!isExplanationExpanded)}
              className="w-full flex items-center justify-between p-3 hover:bg-blue-100/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <HelpCircle size={16} className="text-blue-600" />
                <h4 className="font-semibold text-blue-900 text-sm">Explanation</h4>
              </div>
              <span className="text-blue-600 text-xs">{isExplanationExpanded ? '▼' : '▶'}</span>
            </button>
            {isExplanationExpanded && (
              <div className="p-4 pt-0 border-t border-blue-200">
                <p className="text-blue-800 text-sm leading-relaxed">{question.explanation}</p>
              </div>
            )}
          </div>
        )}

        {showAnswer && (
          <button onClick={onNext} className={`w-full ${primaryColor} h-12 rounded-xl text-white font-semibold flex items-center justify-center gap-2 shadow-lg hover:opacity-90 transition-all`}>
            {isLastQuestion ? <>Finish Quiz <CheckCircle2 size={18} /></> : <>Next Question <ArrowRight size={18} /></>}
          </button>
        )}
      </div>
    );
  }

  // ====================== FULL SCREEN MODE ======================
  return (
    <div className="h-[calc(100vh-90px)] overflow-hidden flex items-center justify-center p-3 md:p-5 relative">
      <div className="w-full max-w-5xl h-full bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 border-b bg-white/90 backdrop-blur-sm">
          <div className="p-4 md:p-6 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">Question</p>
              <h2 className="text-2xl md:text-3xl font-bold">{currentQuestion + 1} / {totalQuestions}</h2>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">Score</p>
                <h2 className="text-2xl md:text-3xl font-bold">{score}</h2>
              </div>

              <button
                onClick={toggleEmergencyPause}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-medium transition-all ${
                  isEmergencyPaused ? "bg-amber-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"
                }`}
              >
                {isEmergencyPaused ? <Play size={22} /> : <Pause size={22} />}
                <span className="hidden md:inline">{isEmergencyPaused ? "Resume" : "Pause"}</span>
              </button>
            </div>
          </div>

          <div className="mx-6 h-2 rounded-full bg-gray-100 overflow-hidden -mt-1">
            <div className={`h-full transition-all duration-500 ${primaryColor}`} style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar relative">
          {isEmergencyPaused && (
            <div className="absolute inset-0 bg-black/70 z-50 flex items-center justify-center">
              <div className="text-center text-white">
                <Pause size={80} className="mx-auto mb-6" />
                <h2 className="text-4xl font-bold mb-3">Quiz Paused</h2>
                <p className="text-xl opacity-75">Click the pause button to resume</p>
              </div>
            </div>
          )}

          <div className="p-4 md:p-7">
            {/* Question */}
            <div className="mb-8 w-full rounded-2xl border p-6 bg-yellow-50 shadow-sm">
              <div className="flex justify-end mb-4">
                <button onClick={toggleSpeech} className="px-5 py-3 text-gray-500 hover:text-gray-700 transition rounded-xl hover:bg-white">
                  {isSpeaking ? <Volume2 className="w-6 h-6 text-green-600 animate-pulse" /> : <Volume2 className="w-6 h-6" />}
                </button>
              </div>
              <h3 className={`${detectTextInfo(question.question).direction === "rtl" ? "text-right text-4xl font-arabic font-dhivehi font-extrabold" : "text-left text-xl font-semibold"} leading-relaxed break-words`}>
                {question.question}
              </h3>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {question.options.map((option, index) => {
                const normalized = normalizeOption(option);
                const value = getOptionValue(option);
                const label = getOptionLabel(option);
                const icon = getOptionIcon(option);
                const imageUrl = getOptionImage(option);
                const imageAlt = getOptionImageAlt(option);
                const isCorrect = value === question.answer;
                const isSelected = value === selected;

                let optionClasses = "relative w-full rounded-2xl border p-5 text-left transition-all duration-200 flex items-center justify-between gap-4 hover:shadow-md";

                if (showAnswer) {
                  if (isCorrect) optionClasses += " bg-green-50 border-green-500 ring-1 ring-green-400";
                  else if (isSelected) optionClasses += " bg-red-50 border-red-500 ring-1 ring-red-400";
                  else optionClasses += " bg-gray-50 border-gray-200";
                } else {
                  optionClasses += " hover:border-gray-400 bg-white cursor-pointer";
                }

                return (
                  <button
                    key={value || index}
                    onClick={() => handleOptionSelect(option)}
                    disabled={showAnswer || isEmergencyPaused}
                    className={optionClasses}
                  >
                    <div dir={detectTextInfo(label).direction} className="flex items-center gap-4 flex-1">
                      <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${!showAnswer ? 'bg-gray-100 text-gray-700' : isCorrect ? 'bg-green-500 text-white' : isSelected ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                        {String.fromCharCode(65 + index)}
                      </span>
                      
                      <span className={`${detectTextInfo(label).direction === "rtl" ? "text-right text-3xl font-arabic font-dhivehi font-extrabold" : "text-left"} text-base md:text-lg font-medium break-words`}>
                        {label}
                      </span>
                    </div>

                    {showAnswer && isCorrect && <CheckCircle2 size={24} className="text-green-600 shrink-0" />}
                    {showAnswer && isSelected && !isCorrect && <XCircle size={24} className="text-red-600 shrink-0" />}
                  </button>
                );
              })}
            </div>

            {/* Timer */}
            {!showAnswer && (
              <div className="flex items-center gap-4 mb-8">
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-red-500 transition-all duration-1000" style={{ width: `${(timeLeft / 15) * 100}%` }} />
                </div>
                <span className="font-mono font-bold text-xl tabular-nums">⏱ {timeLeft}</span>
                <button onClick={toggleTimer} className="p-3 bg-gray-100 hover:bg-gray-200 rounded-2xl transition" disabled={isEmergencyPaused}>
                  {timerStopped ? <PlayCircle size={22} /> : <PauseCircle size={22} />}
                </button>
              </div>
            )}

            {/* Explanation */}
            {showAnswer && question.explanation && (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle size={22} className="text-blue-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-2 text-lg">Explanation</h3>
                    <p className="text-blue-800 leading-relaxed">{question.explanation}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Next Button */}
            {showAnswer && (
              <div className="flex justify-end">
                <button
                  onClick={onNext}
                  className={`px-10 ${primaryColor} h-14 rounded-2xl text-white font-semibold flex items-center gap-3 text-lg shadow-lg hover:opacity-90 transition-all`}
                >
                  {isLastQuestion ? "Finish Quiz" : "Next Question"}
                  <ArrowRight size={22} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Result Overlay */}
      {showResult && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-50">
          <div className="bg-white rounded-3xl p-10 shadow-2xl text-center max-w-xs w-full animate-[bounce_0.6s]">
            {correctAnswer ? (
              <>
                <CheckCircle2 size={72} className="text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-green-600">Correct!</h2>
              </>
            ) : (
              <>
                <XCircle size={72} className="text-red-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-red-600">Wrong!</h2>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}